<svelte:options runes={false} />
<script>
  // Per-execution shortfall rows (TASK-K2T-15): IS at arrival + the terminal-anchored post-trade
  // horizons (+5/15/30/60m), each priced from the witness tape. A horizon still pending renders the
  // dash (stamps land up to an hour after terminal — honest absence, never a fake zero). ArrivalSource
  // is printed because a mark's provenance is part of its meaning (tape vs none).
  import { fmtBps, bpsTone, policyLabel } from '../../../lib/tca.js';
  import { displaySymbol as display } from '../../../lib/integrity.js';
  import { formatNum } from '../../../lib/lineage.js';

  export let executions = [];

  function fmtTime(t) {
    const d = new Date(t);
    return Number.isNaN(d.getTime()) ? '—' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  }
</script>

{#if (executions ?? []).length === 0}
  <div class="empty-state"><p class="empty-state-description">No executions in the window.</p></div>
{:else}
  <div class="tca-scroll">
    <table>
      <thead>
        <tr>
          <th>Arrival</th><th>Symbol</th><th>Side</th><th>Policy</th><th>Status</th>
          <th class="num">Arr mid</th><th class="num">Avg fill</th><th class="num">Qty</th>
          <th class="num">IS arr</th><th class="num">+5m</th><th class="num">+15m</th><th class="num">+30m</th><th class="num">+60m</th>
          <th>Src</th>
        </tr>
      </thead>
      <tbody>
        {#each executions as e (e.clientOrderId)}
          <tr>
            <td class="num">{fmtTime(e.arrivalAt)}</td>
            <td>{display(e.symbol)}</td>
            <td class="tca-side-{String(e.side).toLowerCase()}">{e.side}</td>
            <td>{policyLabel(e.policy)}</td>
            <td>{e.status}</td>
            <td class="num">{e.arrivalMid != null ? formatNum(e.arrivalMid, 2) : '—'}</td>
            <td class="num">{e.avgFillPrice != null ? formatNum(e.avgFillPrice, 2) : '—'}</td>
            <td class="num">{e.filledQty != null ? formatNum(e.filledQty, 8) : '—'}</td>
            <td class="num {bpsTone(e.isArrivalBps)}">{fmtBps(e.isArrivalBps)}</td>
            <td class="num">{fmtBps(e.is5mBps)}</td>
            <td class="num">{fmtBps(e.is15mBps)}</td>
            <td class="num">{fmtBps(e.is30mBps)}</td>
            <td class="num">{fmtBps(e.is60mBps)}</td>
            <td class="tca-src">{e.arrivalSource ?? '—'}</td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
  <p class="tca-note">IS at arrival = side-adjusted cost vs the arrival mid (positive = adverse); +h columns are post-trade drift from the FILL after terminal (the reversion convention), priced on the tape.</p>
{/if}

<style>
  .tca-scroll { overflow-x: auto; }
  table { width: 100%; border-collapse: collapse; font-size: 0.72rem; }
  th, td { padding: 0.3rem 0.45rem; border-bottom: 1px solid var(--border-subtle, #1e1f22); white-space: nowrap; text-align: left; }
  th { font-size: 0.64rem; text-transform: uppercase; letter-spacing: 0.05em; color: var(--text-muted, #8a8b8d); }
  th.num, td.num { text-align: right; font-variant-numeric: tabular-nums; }
  .tca-side-buy { color: var(--success, #1aa260); font-weight: 700; }
  .tca-side-sell { color: var(--danger, #e0533a); font-weight: 700; }
  .tca-adverse { color: var(--danger, #e0533a); }
  .tca-favorable { color: var(--success, #1aa260); }
  .tca-src { font-size: 0.64rem; color: var(--text-muted, #8a8b8d); }
  .tca-note { margin: 0.4rem 0 0; font-size: 0.64rem; color: var(--text-muted, #8a8b8d); }
</style>
